import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from "react";
/**
 * NEVER A WHITE SCREEN (22/10/26: Jamie, "when I click on setup on mobile it crashes to a white screen").
 *
 * React leaves the whole page blank when a screen throws while drawing and nothing catches it. This catches it:
 *  - A page's code that can't be fetched (the app was opened before a deploy, and its old page files are gone) is not
 *    a fault: the app reloads itself once, onto the new version.
 *  - Anything else shows a short message with the error itself (so a screenshot says what broke), Reload and Home,
 *    and the error is reported to the server's log (POST /api/client-error) so it can be found without a screenshot.
 * The company app wraps each page in one (keyed by the address, so moving to another page starts afresh), and the
 * whole app in another; the console the same.
 */
const RELOADED = "nozl-reloaded-for";
export const isStaleCode = (e) => /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch module|Unable to preload CSS|MIME type/i.test(String(e?.message ?? e));
/** Reload once for this address after a deploy; never in a loop (a second failure within a minute shows the message). */
export function reloadForNewVersion() {
    try {
        const last = JSON.parse(sessionStorage.getItem(RELOADED) ?? "null");
        if (last && last.path === location.pathname && Date.now() - last.at < 60_000)
            return false;
        sessionStorage.setItem(RELOADED, JSON.stringify({ path: location.pathname, at: Date.now() }));
    }
    catch { /* private mode: reload anyway, once per page load */ }
    location.reload();
    return true;
}
export function reportClientError(error, where) {
    try {
        const e = error;
        const body = JSON.stringify({ where, path: location.pathname, message: String(e?.message ?? error).slice(0, 500), stack: String(e?.stack ?? "").slice(0, 2000), ua: navigator.userAgent.slice(0, 200) });
        void fetch("/api/client-error", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true, credentials: "same-origin" }).catch(() => undefined);
    }
    catch { /* reporting must never throw */ }
}
// A page file that fails to load before React asks for it (Vite's own preload) gets the same once-only reload.
if (typeof window !== "undefined") {
    window.addEventListener("vite:preloadError", (ev) => { if (reloadForNewVersion())
        ev.preventDefault(); });
}
export class PageError extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) {
        if (isStaleCode(error) && reloadForNewVersion())
            return;
        reportClientError(Object.assign(error, { stack: `${error.stack ?? ""}\n--- component ---${info.componentStack ?? ""}` }), this.props.where);
    }
    render() {
        const { error } = this.state;
        if (!error)
            return this.props.children;
        const stale = isStaleCode(error);
        return (_jsx("div", { className: "tc-content", children: _jsx("div", { className: "tc-card tc-page-error", role: "alert", children: _jsxs("div", { className: "tc-card-body", children: [_jsx("h2", { children: stale ? "Nozl has been updated" : "This page hit a problem" }), _jsx("p", { className: "muted", children: stale ? "Reload to get the new version." : "It's been reported to Nozl. Reloading usually fixes it; if it doesn't, send a screenshot of this." }), !stale && _jsx("p", { className: "tc-page-error-msg", children: _jsx("code", { children: String(error.message || error).slice(0, 300) }) }), _jsxs("div", { className: "tc-form-actions", children: [_jsx("button", { className: "tc-btn primary", onClick: () => location.reload(), children: "Reload" }), this.props.onHome && _jsx("button", { className: "tc-btn", onClick: () => { this.setState({ error: null }); this.props.onHome(); }, children: "Home" })] })] }) }) }));
    }
}
